/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/admin/GlobalModalsProvider.tsx
 * Release: v1.0.0 - Provider globale per modali admin
 * Data: 2026-01-18
 * Descrizione: Espone funzioni globali per aprire modali da qualsiasi pagina
 */

import React, { useState, useEffect } from 'react';
import { appToast } from '../../lib/appDialog';
import { useAuth } from '../auth/AuthProvider';
import DataCleanupModal from './DataCleanupModal';
import MobileOperatorsModal from './MobileOperatorsModal';
import { DevSettingsPanel } from './DevSettingsPanel';
import WorkflowLogModal from './WorkflowLogModal';
import { WhatsAppStatusBotModal } from '../whatsapp-status-bot';
import SystemConfigModal from './SystemConfigModal';

interface GlobalModalsProviderProps {
  children: React.ReactNode;
}

export const GlobalModalsProvider: React.FC<GlobalModalsProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [isDataCleanupOpen, setIsDataCleanupOpen] = useState(false);
  const [isMobileOperatorsOpen, setIsMobileOperatorsOpen] = useState(false);
  const [isDevSettingsOpen, setIsDevSettingsOpen] = useState(false);
  const [isWorkflowLogOpen, setIsWorkflowLogOpen] = useState(false);
  const [isWhatsAppBotOpen, setIsWhatsAppBotOpen] = useState(false);
  const [isSystemConfigOpen, setIsSystemConfigOpen] = useState(false);

  const isSuperAdmin = user?.role === 'superadmin' || user?.group_id === 'superadmin';
  const isAdmin = isSuperAdmin || user?.role === 'admin';

  // Esponi funzioni globali per aprire le modali
  useEffect(() => {
    (window as any).openDataCleanupModal = () => {
      if (isSuperAdmin) {
        setIsDataCleanupOpen(true);
      } else {
        appToast.error('❌ Accesso negato. Richiesto ruolo superadmin.');
      }
    };
    (window as any).openMobileOperatorsModal = () => {
      if (isAdmin) {
        setIsMobileOperatorsOpen(true);
      } else {
        appToast.error('❌ Accesso negato. Richiesto ruolo admin.');
      }
    };
    (window as any).openDevSettings = () => {
      if (isSuperAdmin) {
        setIsDevSettingsOpen(true);
      } else {
        appToast.error('❌ Accesso negato. Richiesto ruolo superadmin.');
      }
    };
    (window as any).openWorkflowLogModal = () => {
      if (isSuperAdmin) {
        setIsWorkflowLogOpen(true);
      } else {
        appToast.error('❌ Accesso negato. Richiesto ruolo superadmin.');
      }
    };
    (window as any).openWhatsAppBotModal = () => {
      if (isAdmin || isSuperAdmin) {
        setIsWhatsAppBotOpen(true);
      } else {
        appToast.error('❌ Accesso negato. Richiesto ruolo admin o superadmin.');
      }
    };
    (window as any).openSystemConfigModal = () => {
      if (isAdmin || isSuperAdmin) {
        setIsSystemConfigOpen(true);
      } else {
        appToast.error('❌ Accesso negato. Richiesto ruolo admin o superadmin.');
      }
    };

    return () => {
      delete (window as any).openDataCleanupModal;
      delete (window as any).openMobileOperatorsModal;
      delete (window as any).openDevSettings;
      delete (window as any).openWorkflowLogModal;
      delete (window as any).openWhatsAppBotModal;
      delete (window as any).openSystemConfigModal;
    };
  }, [isSuperAdmin, isAdmin]);

  return (
    <>
      {children}

      {/* Modale Data Cleanup (solo superadmin) */}
      {isSuperAdmin && (
        <DataCleanupModal
          isOpen={isDataCleanupOpen}
          onClose={() => setIsDataCleanupOpen(false)}
        />
      )}

      {/* Modale Gestione Operatori Mobile (admin e superadmin) */}
      {isAdmin && (
        <MobileOperatorsModal
          isOpen={isMobileOperatorsOpen}
          onClose={() => setIsMobileOperatorsOpen(false)}
        />
      )}

      {/* Pannello Impostazioni Dev - toggle mock/live data (solo superadmin) */}
      {isSuperAdmin && (
        <DevSettingsPanel
          isOpen={isDevSettingsOpen}
          onClose={() => setIsDevSettingsOpen(false)}
        />
      )}

      {/* Modale Cronologia Workflow (solo superadmin) */}
      {isSuperAdmin && (
        <WorkflowLogModal
          isOpen={isWorkflowLogOpen}
          onClose={() => setIsWorkflowLogOpen(false)}
        />
      )}

      {/* Modale WhatsApp Status Bot (admin e superadmin) */}
      {(isAdmin || isSuperAdmin) && (
        <WhatsAppStatusBotModal
          isOpen={isWhatsAppBotOpen}
          onClose={() => setIsWhatsAppBotOpen(false)}
        />
      )}

      {/* Modale Impostazioni Sistema (admin e superadmin) */}
      {(isAdmin || isSuperAdmin) && (
        <SystemConfigModal
          isOpen={isSystemConfigOpen}
          onClose={() => setIsSystemConfigOpen(false)}
        />
      )}
    </>
  );
};

export default GlobalModalsProvider;
